import React, {useCallback, useMemo, useState} from 'react';
import {
  FlatList,
  Pressable,
  View,
  ScrollView,
  StyleSheet,
  TextInput,
} from 'react-native';
import Svg, {Circle, Path} from 'react-native-svg';
import CCText from '@/Atoms/CCText';
import BackToPrevious from '@/Atoms/BackToPrevious';
import {useClubDirectory, useMyClubs} from '@/Hooks/ClubsApi/useClubsQueries';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import ClubCard from './components/ClubCard';
import ClubCardSkeleton from './components/ClubCardSkeleton';
import FilterSheet, {
  ClubFilters,
  activeFilterList,
} from './components/FilterSheet';

const CREAM = '#FAF6EB';
const AQUA = '#3DAB9E';
const YELLOW = '#FACF47';
const BROWN = '#4D3F37';
const HEADING = '#262322';
const GREY = '#888280';

// Search / filter glyphs (design uses thin stroked line-icons).
function SearchIcon({size = 18}: {size?: number}) {
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={GREY}
      strokeWidth={2}>
      <Circle cx="11" cy="11" r="7" />
      <Path d="m21 21-4.3-4.3" strokeLinecap="round" />
    </Svg>
  );
}
function FilterIcon({
  color = BROWN,
  size = 15,
}: {
  color?: string;
  size?: number;
}) {
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round">
      <Path d="M4 6h16M7 12h10M10 18h4" />
    </Svg>
  );
}

export default function Clubs({navigation}: {navigation: any}) {
  const [tab, setTab] = useState<'mine' | 'browse'>('mine');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ClubFilters>({
    band: null,
    activity: null,
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const activeFilters = activeFilterList(filters);
  const mine = useMyClubs();
  const directory = useClubDirectory(search, filters);
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;

  const dirCount = directory.data?.clubs?.length ?? 0;

  const TabBtn = useCallback(
    ({id, label}: {id: 'mine' | 'browse'; label: string}) => {
      const active = tab === id;
      // Browse desktop uses the yellow underline; mobile + My-Clubs use aqua.
      const underline = isWide ? YELLOW : AQUA;
      return (
        <Pressable
          testID={`clubs-tab-${id}`}
          onPress={() => setTab(id)}
          style={{
            paddingVertical: 14,
            paddingBottom: 13,
            marginRight: 24,
            borderBottomWidth: 2.5,
            borderBottomColor: active ? underline : 'transparent',
            marginBottom: -1,
          }}>
          <CCText
            style={{
              fontWeight: '600',
              color: active ? HEADING : GREY,
              fontSize: 15,
            }}>
            {label}
          </CCText>
        </Pressable>
      );
    },
    [tab, isWide],
  );

  // Browse (desktop): 3-column grid of stacked cards. My Clubs (desktop): full-
  // width rows (matches the design's D:HOME workspace).
  const renderGridCard = (
    item: any,
    targetScreen: string,
    cardVariant: 'mine' | 'browse',
  ) => (
    <View
      key={item.id}
      style={
        isWide
          ? {width: '33.33%', padding: 9}
          : {width: '100%', marginBottom: 12}
      }>
      <ClubCard
        club={item}
        onPress={() => navigation.navigate(targetScreen, {clubId: item.id})}
        testID={`club-card-${item.id}`}
        variant={cardVariant}
        layout={isWide ? 'grid' : 'row'}
      />
    </View>
  );

  const searchBar = (
    <View style={styles.searchBar}>
      <SearchIcon />
      <TextInput
        value={search}
        onChangeText={setSearch}
        placeholder="Search clubs by name"
        placeholderTextColor={GREY}
        testID={isWide ? 'clubs-search-wide' : 'clubs-search'}
        // @ts-ignore web-only: remove the browser's default input outline
        style={[
          {
            flex: 1,
            fontSize: 15,
            color: HEADING,
            marginLeft: 8,
            paddingVertical: 0,
          },
          {outlineStyle: 'none'},
        ]}
      />
    </View>
  );

  const hasFilters = activeFilters.length > 0;
  const filterRow = (
    <View style={styles.filterRow}>
      <Pressable
        testID="clubs-filters"
        style={[styles.filterBtn, hasFilters && styles.filterBtnActive]}
        onPress={() => setFilterOpen(true)}>
        <FilterIcon color={hasFilters ? '#fff' : BROWN} />
        <CCText style={[styles.filterBtnText, hasFilters && {color: '#fff'}]}>
          Filters
        </CCText>
      </Pressable>
      {hasFilters ? (
        <View style={styles.activeChips}>
          {activeFilters.map(f => (
            <Pressable
              key={f.key}
              testID={`filter-active-${f.key}`}
              onPress={() => setFilters(cur => ({...cur, [f.key]: null}))}
              style={styles.activeChip}>
              <CCText
                style={{fontSize: 12, fontWeight: '600', color: '#2C7B70'}}>
                {f.label} ✕
              </CCText>
            </Pressable>
          ))}
        </View>
      ) : null}
      <CCText style={{fontSize: 12.5, color: GREY, fontWeight: '600'}}>
        {dirCount} {dirCount === 1 ? 'club' : 'clubs'}
      </CCText>
    </View>
  );

  return (
    <View style={{flex: 1, backgroundColor: CREAM}}>
      {/* Header */}
      <View
        style={{
          paddingHorizontal: isWide ? 32 : 20,
          paddingTop: isWide ? 20 : 12,
          paddingBottom: 10,
          flexDirection: isWide ? 'row' : 'column',
          alignItems: isWide ? 'flex-end' : undefined,
          justifyContent: isWide ? 'space-between' : undefined,
          gap: isWide ? 20 : 0,
        }}>
        <View>
          {/* Clubs had no way back at all — the left menu was the only exit.
              Names wherever you actually came from, not a fixed destination.
              ClubRoom excluded: its own back button always forward-navigates
              HERE, so a Dashboard → ClubRoom → Clubs trip recorded ClubRoom as
              the "previous" screen — pressing back bounced into ClubRoom,
              whose own back button bounced right back to Clubs, forever,
              without ever reaching Dashboard (or wherever the user actually
              started). Same reasoning ClubProfile already excludes it for. */}
          <BackToPrevious
            navigation={navigation}
            exclude={['ClubRoom']}
            style={{marginBottom: 10}}
          />
          <CCText
            fontWeight="thunder"
            style={{fontSize: 30, color: HEADING, lineHeight: 32}}>
            Clubs
          </CCText>
          <CCText style={{fontSize: 13, color: GREY, marginTop: 3}}>
            Chat, compete, and climb activity leaderboards.
          </CCText>
        </View>
        {isWide ? <View style={{width: 300}}>{searchBar}</View> : null}
      </View>

      {/* Tabs */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          borderBottomWidth: 1,
          borderBottomColor: 'rgba(38,35,34,0.10)',
          paddingHorizontal: isWide ? 32 : 20,
        }}>
        <TabBtn id="mine" label="My Clubs" />
        <TabBtn id="browse" label={isWide ? 'Browse Clubs' : 'Browse'} />
        {isWide ? (
          <>
            <View style={{flex: 1}} />
            <Pressable
              testID="clubs-create-btn-wide"
              onPress={() => navigation.navigate('CreateClub')}
              style={styles.createBtnWide}>
              <CCText style={{color: '#fff', fontSize: 16, lineHeight: 18}}>
                +
              </CCText>
              <CCText style={{color: '#fff', fontSize: 14, fontWeight: '600'}}>
                Create a club
              </CCText>
            </Pressable>
          </>
        ) : null}
      </View>

      {/* Content */}
      <View style={{flex: 1}}>
        {tab === 'mine' ? (
          mine.isLoading ? (
            <View
              style={{
                paddingHorizontal: isWide ? 32 : 20,
                paddingTop: 16,
              }}>
              <ClubCardSkeleton
                count={isWide ? 3 : 2}
                layout={isWide ? 'grid' : 'list'}
              />
            </View>
          ) : (mine.data?.clubs?.length ?? 0) === 0 ? (
            <ScrollView contentContainerStyle={styles.emptyScroll}>
              <View style={styles.emptyPlate}>
                <CCText style={{fontSize: 78}}>♟</CCText>
              </View>
              <CCText
                fontWeight="thunder"
                style={{
                  fontSize: 26,
                  color: HEADING,
                  textAlign: 'center',
                  marginBottom: 10,
                }}>
                Find your people
              </CCText>
              <CCText
                style={{
                  fontSize: 14,
                  color: GREY,
                  textAlign: 'center',
                  marginBottom: 20,
                  lineHeight: 20,
                  maxWidth: 260,
                }}>
                Join a club to chat, play together, and climb activity
                leaderboards. Rating doesn't matter here.
              </CCText>
              <View style={{width: '100%', maxWidth: 280, gap: 12}}>
                <Pressable
                  onPress={() => setTab('browse')}
                  style={styles.pillPrimary}>
                  <CCText
                    style={{color: BROWN, fontSize: 16, fontWeight: '600'}}>
                    Browse clubs
                  </CCText>
                </Pressable>
                <Pressable
                  onPress={() => navigation.navigate('CreateClub')}
                  style={styles.outlineBtn}>
                  <CCText
                    style={{color: BROWN, fontSize: 15, fontWeight: '600'}}>
                    Create your own
                  </CCText>
                </Pressable>
              </View>
            </ScrollView>
          ) : isWide ? (
            <ScrollView contentContainerStyle={{padding: 32, paddingTop: 16}}>
              <View
                style={{
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  marginHorizontal: -9,
                }}>
                {mine.data!.clubs.map(item =>
                  renderGridCard(item, 'ClubRoom', 'mine'),
                )}
              </View>
            </ScrollView>
          ) : (
            <FlatList
              data={mine.data!.clubs}
              keyExtractor={c => String(c.id)}
              contentContainerStyle={{
                paddingHorizontal: 20,
                paddingTop: 16,
                paddingBottom: 96,
                gap: 12,
              }}
              renderItem={({item}) => (
                <ClubCard
                  club={item}
                  onPress={() =>
                    navigation.navigate('ClubRoom', {clubId: item.id})
                  }
                  testID={`club-card-${item.id}`}
                  variant="mine"
                />
              )}
            />
          )
        ) : (
          <View
            style={{
              flex: 1,
              paddingHorizontal: isWide ? 32 : 20,
              paddingTop: 14,
            }}>
            {!isWide ? searchBar : null}
            <View style={{marginTop: !isWide ? 11 : 0}}>{filterRow}</View>

            {directory.isLoading ? (
              <View style={{paddingTop: 14}}>
                <ClubCardSkeleton
                  count={3}
                  layout={isWide ? 'grid' : 'list'}
                />
              </View>
            ) : dirCount === 0 ? (
              <ScrollView contentContainerStyle={styles.emptyScroll}>
                <CCText style={{fontSize: 40, marginBottom: 8}}>♟</CCText>
                <CCText
                  style={{
                    fontSize: 14,
                    fontWeight: '600',
                    color: BROWN,
                    marginBottom: 6,
                  }}>
                  No clubs match
                </CCText>
                <CCText
                  style={{
                    fontSize: 12.5,
                    color: GREY,
                    textAlign: 'center',
                    marginBottom: 16,
                    maxWidth: 220,
                  }}>
                  Try fewer filters, or search a private club by its exact name.
                </CCText>
                {search ? (
                  <Pressable
                    onPress={() => setSearch('')}
                    style={styles.clearBtn}>
                    <CCText
                      style={{color: '#fff', fontSize: 13, fontWeight: '600'}}>
                      Clear search
                    </CCText>
                  </Pressable>
                ) : null}
              </ScrollView>
            ) : isWide ? (
              <ScrollView contentContainerStyle={{paddingVertical: 14}}>
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    marginHorizontal: -9,
                  }}>
                  {directory.data!.clubs.map(item =>
                    renderGridCard(item, 'ClubProfile', 'browse'),
                  )}
                </View>
              </ScrollView>
            ) : (
              <FlatList
                data={directory.data?.clubs ?? []}
                keyExtractor={c => String(c.id)}
                contentContainerStyle={{
                  paddingTop: 14,
                  paddingBottom: 96,
                  gap: 11,
                }}
                renderItem={({item}) => (
                  <ClubCard
                    club={item}
                    onPress={() =>
                      navigation.navigate('ClubProfile', {clubId: item.id})
                    }
                    testID={`club-card-${item.id}`}
                  />
                )}
              />
            )}
          </View>
        )}
      </View>

      {/* Filter sheet */}
      <FilterSheet
        visible={filterOpen}
        filters={filters}
        count={dirCount}
        onChange={setFilters}
        onClear={() => setFilters({band: null, activity: null})}
        onClose={() => setFilterOpen(false)}
      />

      {/* FAB — Create a club (mobile only) */}
      {!isWide ? (
        <Pressable
          testID="clubs-create-btn"
          onPress={() => navigation.navigate('CreateClub')}
          style={styles.fab}>
          <CCText style={{color: '#fff', fontSize: 18, lineHeight: 18}}>
            +
          </CCText>
          <CCText style={{color: '#fff', fontSize: 14, fontWeight: '600'}}>
            Create a club
          </CCText>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 11,
  },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  filterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.20)',
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  filterBtnActive: {backgroundColor: AQUA, borderColor: AQUA},
  filterBtnText: {fontSize: 13, fontWeight: '600', color: BROWN},
  activeChips: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  activeChip: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0FAF8',
    borderWidth: 1,
    borderColor: '#B8E0DA',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 10,
  },
  createBtnWide: {
    marginBottom: 8,
    backgroundColor: AQUA,
    borderRadius: 999,
    paddingVertical: 9,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  centerFill: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  emptyScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 40,
  },
  emptyPlate: {
    width: 150,
    height: 150,
    borderRadius: 36,
    backgroundColor: '#FFF0C8',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 14,
  },
  pillPrimary: {
    backgroundColor: YELLOW,
    paddingVertical: 15,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  outlineBtn: {
    paddingVertical: 13,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: BROWN,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtn: {
    backgroundColor: AQUA,
    paddingVertical: 9,
    paddingHorizontal: 16,
    borderRadius: 10,
    alignItems: 'center',
  },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: AQUA,
    paddingVertical: 13,
    paddingHorizontal: 18,
    borderRadius: 32,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 8},
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 6,
  },
});
